import { supabase } from './supabase'
import { offlineDB } from './offlineDB'

export const dataService = {
  // ==================== CLIENTES ====================
  
  async getClients(soloActivos = true) {
    if (navigator.onLine) {
      try {
        let query = supabase.from('clients').select('*').order('alias')
        if (soloActivos) query = query.eq('activo', true)
        
        const { data, error } = await query
        
        if (!error && data) {
          // Solo cachear los activos para uso offline
          const activos = data.filter(c => c.activo)
          await offlineDB.cacheClients(activos)
          return { data, error: null, source: 'online' }
        }
      } catch (err) {
        console.log('Error online, usando caché')
      }
    }

    const cached = await offlineDB.getCachedClients()
    return { data: cached, error: null, source: 'cache' }
  },

  async saveClient(client, isNew) {
    const operation = isNew ? 'insert' : 'update'
    
    if (navigator.onLine) {
      try {
        if (isNew) {
          const { data, error } = await supabase
            .from('clients')
            .insert(client)
            .select()
            .single()
          
          if (error) throw error
          return { success: true, data, mode: 'online' }
        } else {
          const { data, error } = await supabase
            .from('clients')
            .update(client)
            .eq('id', client.id)
            .select()
            .single()
          
          if (error) throw error
          return { success: true, data, mode: 'online' }
        }
      } catch (err) {
        console.log('Error guardando online, guardando offline...', err)
      }
    }

    await offlineDB.saveClientOffline(client, operation)
    return { success: true, data: client, mode: 'offline' }
  },

  async deleteClient(id) {
    // Marcar como inactivo en lugar de borrar físicamente
    // para preservar el historial de albaranes y facturas
    if (navigator.onLine) {
      try {
        const { error } = await supabase
          .from('clients')
          .update({ activo: false })
          .eq('id', id)
        
        if (error) throw error
        return { success: true, mode: 'online' }
      } catch (err) {
        console.log('Error marcando inactivo online, guardando offline...', err)
      }
    }

    await offlineDB.saveClientOffline({ id, activo: false }, 'update')
    return { success: true, mode: 'offline' }
  },

  // ==================== PRODUCTOS ====================
  
  async getProducts(soloActivos = true) {
    if (navigator.onLine) {
      try {
        let query = supabase.from('products').select('*').order('nombre')
        if (soloActivos) query = query.eq('activo', true)
        
        const { data, error } = await query
        
        if (!error && data) {
          // Solo cachear los activos para uso offline
          const activos = data.filter(p => p.activo)
          await offlineDB.cacheProducts(activos)
          return { data, error: null, source: 'online' }
        }
      } catch (err) {
        console.log('Error online, usando caché')
      }
    }

    const cached = await offlineDB.getCachedProducts()
    return { data: cached, error: null, source: 'cache' }
  },

  async saveProduct(product, isNew) {
    const operation = isNew ? 'insert' : 'update'
    
    if (navigator.onLine) {
      try {
        if (isNew) {
          const { data, error } = await supabase
            .from('products')
            .insert(product)
            .select()
            .single()
          
          if (error) throw error
          return { success: true, data, mode: 'online' }
        } else {
          const { data, error } = await supabase
            .from('products')
            .update(product)
            .eq('id', product.id)
            .select()
            .single()
          
          if (error) throw error
          return { success: true, data, mode: 'online' }
        }
      } catch (err) {
        console.log('Error guardando online, guardando offline...', err)
      }
    }

    await offlineDB.saveProductOffline(product, operation)
    return { success: true, data: product, mode: 'offline' }
  },

  async deleteProduct(id) {
    // Marcar como inactivo en lugar de borrar físicamente
    // para preservar el historial de albaranes y facturas
    if (navigator.onLine) {
      try {
        const { error } = await supabase
          .from('products')
          .update({ activo: false })
          .eq('id', id)
        
        if (error) throw error
        return { success: true, mode: 'online' }
      } catch (err) {
        console.log('Error marcando inactivo online, guardando offline...', err)
      }
    }

    await offlineDB.saveProductOffline({ id, activo: false }, 'update')
    return { success: true, mode: 'offline' }
  },

  // ==================== PLANTILLAS ====================
  
  async getClientTemplates(clientId) {
    if (navigator.onLine) {
      try {
        const { data, error } = await supabase
          .from('client_templates')
          .select('*')
          .eq('client_id', clientId)
          .eq('activo', true)
        
        if (!error && data) {
          return { data, error: null, source: 'online' }
        }
      } catch (err) {
        console.log('Error online, usando caché')
      }
    }

    const cached = await offlineDB.getCachedTemplates(clientId)
    return { data: cached, error: null, source: 'cache' }
  },

  async saveClientTemplates(clientId, templates) {
    if (navigator.onLine) {
      try {
        await supabase
          .from('client_templates')
          .delete()
          .eq('client_id', clientId)

        if (templates && templates.length > 0) {
          const { error } = await supabase
            .from('client_templates')
            .insert(templates)
          
          if (error) throw error
        }

        return { success: true, mode: 'online' }
      } catch (err) {
        console.log('Error guardando plantillas online, guardando offline...', err)
      }
    }

    await offlineDB.deleteTemplatesByClient(clientId)
    
    for (const template of templates) {
      const templateWithId = {
        ...template,
        id: template.id || `temp_${Date.now()}_${Math.random()}`
      }
      await offlineDB.put('client_templates', templateWithId)
    }

    await offlineDB.addPendingOperation({
      type: 'template',
      operation: 'insert_batch',
      data: { client_id: clientId, templates }
    })

    return { success: true, mode: 'offline' }
  },

  // ==================== ALBARANES ====================
  
  async saveDelivery(deliveryData, existingDeliveryId = null) {
    const isUpdate = !!existingDeliveryId
    
    if (navigator.onLine) {
      try {
        if (isUpdate) {
          console.log('🔄 ACTUALIZANDO albarán existente:', existingDeliveryId)
          
          await supabase
            .from('delivery_lines')
            .delete()
            .eq('delivery_id', existingDeliveryId)
          
          if (deliveryData.lines && deliveryData.lines.length > 0) {
            const lineasParaInsertar = deliveryData.lines.map(l => ({
              delivery_id: existingDeliveryId,
              ...l
            }))

            const { error: linesError } = await supabase
              .from('delivery_lines')
              .insert(lineasParaInsertar)

            if (linesError) throw linesError
          }

          return { success: true, deliveryId: existingDeliveryId, mode: 'online', isUpdate: true }
        } else {
          console.log('✨ CREANDO albarán nuevo')
          
          const { data: newDelivery, error: deliveryError } = await supabase
            .from('deliveries')
            .insert({
              client_id: deliveryData.client_id,
              fecha: deliveryData.fecha,
              ruta: deliveryData.ruta,
              estado: 'entregado'
            })
            .select('id')
            .single()

          if (deliveryError) throw deliveryError

          if (deliveryData.lines && deliveryData.lines.length > 0) {
            const lineasParaInsertar = deliveryData.lines.map(l => ({
              delivery_id: newDelivery.id,
              ...l
            }))

            const { error: linesError } = await supabase
              .from('delivery_lines')
              .insert(lineasParaInsertar)

            if (linesError) throw linesError
          }

          return { success: true, deliveryId: newDelivery.id, mode: 'online', isUpdate: false }
        }
      } catch (err) {
        console.log('Error guardando online, guardando offline...', err)
      }
    }

    const offlineId = await offlineDB.saveDeliveryOffline(deliveryData, existingDeliveryId)
    return { success: true, deliveryId: offlineId, mode: 'offline', isUpdate }
  },

  // Compatibilidad con App.jsx — el móvil usa navigator.onLine directamente
  setOnlineStatus(isOnline) {
    // no-op: esta versión móvil no necesita estado manual de conexión
  },

  async getDeliveryByDate(clientId, fecha) {
    if (navigator.onLine) {
      try {
        const { data, error } = await supabase
          .from('deliveries')
          .select('id, delivery_lines(*)')
          .eq('client_id', clientId)
          .eq('fecha', fecha)
          .maybeSingle()
        
        if (!error) {
          return { data, error: null }
        }
      } catch (err) {
        console.log('Error consultando online')
      }
    }

    const deliveries = await offlineDB.getDeliveriesOffline()
    const found = deliveries.find(d => 
      d.client_id === clientId && d.fecha === fecha
    )
    
    if (found) {
      return { 
        data: {
          id: found.id,
          delivery_lines: found.lines || []
        },
        error: null 
      }
    }

    return { data: null, error: null }
  }
}
